import React, { memo, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useInView, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import {
  AirplaneTilt,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Bed,
  Bell,
  Brain,
  CalendarBlank,
  Camera,
  ChartLineUp,
  Check,
  CheckCircle,
  ClipboardText,
  DeviceMobile,
  FileText,
  Heart,
  ImageSquare,
  ListChecks,
  MagicWand,
  MapPin,
  Palette,
  PaperPlaneTilt,
  Receipt,
  ShieldCheck,
  Sparkle,
  Storefront,
  Ticket,
  Users,
  Wallet,
  WhatsappLogo,
} from '@phosphor-icons/react';
import GalagoLogo from '../components/GalagoLogo';
import { WEB_INVITE_TEMPLATES } from '../features/webInvite/templates';
import './LandingPageV3.css';

const FEATURED_WEB_INVITE_IDS = [
  'galago.swan-garden-romance',
  'galago.south-indian-temple-brass',
  'galago.royal-tapestry',
  'galago.kashi-riverlight',
  'galago.birthday-velvet-surprise-60',
];

const WEB_INVITE_SAMPLE_DATA = {
  'galago.swan-garden-romance': {
    eyebrow: 'SAMPLE WEDDING · UDAIPUR',
    headline: 'Mira & Kabir',
    date: '14–16 February 2027',
  },
  'galago.south-indian-temple-brass': {
    eyebrow: 'SAMPLE WEDDING · CHENNAI',
    headline: 'Ananya & Karthik',
    date: '28 January 2027',
  },
  'galago.royal-tapestry': {
    eyebrow: 'SAMPLE WEDDING · JAIPUR',
    headline: 'Leena & Arjun',
    date: '6 December 2026',
  },
  'galago.kashi-riverlight': {
    eyebrow: 'SAMPLE WEDDING · VARANASI',
    headline: 'Ira & Dev',
    date: '21 November 2026',
  },
  'galago.birthday-velvet-surprise-60': {
    eyebrow: 'SAMPLE BIRTHDAY · DELHI',
    headline: 'Maya turns 60',
    date: '18 October 2026',
  },
};

const FEATURED_WEB_INVITES = FEATURED_WEB_INVITE_IDS
  .map((id) => WEB_INVITE_TEMPLATES.find((template) => template.id === id))
  .filter(Boolean)
  .map((template) => ({ ...template, sample: WEB_INVITE_SAMPLE_DATA[template.id] }));

function shouldShowMascotSplash() {
  if (typeof window === 'undefined') return false;

  try {
    const prefersReducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return !prefersReducedMotion;
  } catch (_error) {
    return true;
  }
}

const features = [
  {
    icon: Users,
    title: 'Guests & RSVPs',
    description: 'Import families, organize event access, track plus-ones and responses, and keep every guest detail connected.',
    tone: 'terracotta',
    size: 'wide',
    detail: 'Guest groups · dietary notes · event-wise RSVP',
  },
  {
    icon: CalendarBlank,
    title: 'Itinerary & venues',
    description: 'Build every function with dates, dress codes, maps and directions—then share one reliable schedule.',
    tone: 'marigold',
    detail: 'Multi-day timelines · Google Maps',
  },
  {
    icon: Bed,
    title: 'Rooms, arrivals & travel',
    description: 'Assign rooms, follow guest journeys, manage hotel readiness and bring travel details into the same view.',
    tone: 'sage',
    size: 'tall',
    detail: 'Hotel stays · capacity · check-in ops',
  },
  {
    icon: ListChecks,
    title: 'Tasks & teams',
    description: 'Delegate work by category, surface what needs attention and keep transport, hotel and finance follow-ups moving.',
    tone: 'plum',
    detail: 'Owners · priorities · due dates',
  },
  {
    icon: Wallet,
    title: 'Budgets & claims',
    description: 'Track commitments, invoices, expenses, payments and team claims with approvals and a clear audit trail.',
    tone: 'blue',
    size: 'wide',
    detail: 'Categories · approvals · CSV export',
  },
  {
    icon: Storefront,
    title: 'Vendors & contacts',
    description: 'Keep vendor details, assignments and reusable contacts close to the event work they support.',
    tone: 'rose',
    detail: 'Master vendors · event teams',
  },
  {
    icon: Palette,
    title: 'Invites & stationery',
    description: 'Shape the event theme, create web invites and prepare matching digital or print-ready celebration assets.',
    tone: 'marigold',
    detail: 'Themes · invite studio · stationery',
  },
  {
    icon: Camera,
    title: 'Photos & guest moments',
    description: 'Collect QR uploads, run a live photo wall, curate galleries and help guests find their memories.',
    tone: 'sage',
    size: 'wide',
    detail: 'QR capture · live wall · face matching',
  },
];

const whatsappBenefits = [
  'Send event invites and invite media from the guest list',
  'Nudge pending RSVPs and request travel or ID details',
  'Share itinerary, accommodation and photo updates when they are ready',
  'Review readiness, recipients and delivery attempts in one place',
];

const aiCapabilities = [
  {
    icon: Ticket,
    title: 'Read travel tickets',
    description: 'Turn uploaded flight, train or road tickets into structured journey details for review.',
    preview: 'travel',
  },
  {
    icon: Palette,
    title: 'Develop themes & stationery',
    description: 'Turn a prompt or inspiration image into theme directions, backgrounds and matching stationery.',
    preview: 'theme',
  },
  {
    icon: Receipt,
    title: 'Make finance less manual',
    description: 'Extract invoice fields, suggest budget categories and help organize financial records.',
    preview: 'finance',
  },
  {
    icon: ShieldCheck,
    title: 'Keep content guest-ready',
    description: 'Screen photo-wall uploads for blur, duplicates and unsafe content before they reach the gallery.',
    preview: 'review',
  },
  {
    icon: FileText,
    title: 'Draft event stories',
    description: 'Prepare an editable showcase story from the event’s current details and statistics.',
    preview: 'story',
  },
];

function AiCapabilityPreview({ type }) {
  if (type === 'travel') {
    return (
      <div className="lp-ai-mini lp-ai-mini--travel" aria-hidden="true">
        <span className="lp-ai-mini-label">Sample extraction</span>
        <div><strong>DEL</strong><i /><AirplaneTilt size={15} weight="fill" /><i /><strong>UDR</strong></div>
        <small>17:40 · AI 469</small>
      </div>
    );
  }

  if (type === 'theme') {
    return (
      <div className="lp-ai-mini lp-ai-mini--theme" aria-hidden="true">
        <span className="lp-ai-mini-label">Sample direction</span>
        <div className="lp-ai-mini-theme-card"><b>Moonlit garden</b><span /><span /><span /></div>
      </div>
    );
  }

  if (type === 'finance') {
    return (
      <div className="lp-ai-mini lp-ai-mini--finance" aria-hidden="true">
        <span className="lp-ai-mini-label">Sample invoice read</span>
        <div><span>Venue hire</span><strong>₹42,600</strong></div>
        <div><span>Tax</span><strong>₹7,668</strong></div>
      </div>
    );
  }

  if (type === 'review') {
    return (
      <div className="lp-ai-mini lp-ai-mini--review" aria-hidden="true">
        <span className="lp-ai-mini-label">Sample review</span>
        <div><span><Check size={13} /></span><span><Check size={13} /></span><span><ShieldCheck size={15} /></span></div>
        <small>2 ready · 1 to review</small>
      </div>
    );
  }

  return (
    <div className="lp-ai-mini lp-ai-mini--story" aria-hidden="true">
      <span className="lp-ai-mini-label">Sample editable draft</span>
      <strong>An evening to remember</strong>
      <i /><i /><i />
    </div>
  );
}

const audiences = [
  {
    label: 'For planners',
    icon: ClipboardText,
    title: 'A live operations desk.',
    description: 'See guests, rooms, arrivals, tasks, vendors and money without stitching together five different trackers.',
    items: ['Operational dashboards', 'Team and vendor workflows', 'Company-branded guest experience'],
    tone: 'planner',
  },
  {
    label: 'For hosts',
    icon: Heart,
    title: 'Clarity without the chase.',
    description: 'Stay close to the decisions and the celebration while the working team keeps execution moving.',
    items: ['Shared event visibility', 'Guest and task collaboration', 'Photos, gifts and event details'],
    tone: 'host',
  },
  {
    label: 'For guests',
    icon: DeviceMobile,
    title: 'Everything they need, in context.',
    description: 'Give each guest a focused mobile view of the details that apply to them—before, during and after the event.',
    items: ['Personal itinerary and RSVP', 'Room, travel and directions', 'Live photos and helpful contacts'],
    tone: 'guest',
  },
];

const workflow = [
  { number: '01', title: 'Create the event', text: 'Add the celebration, functions, venues and core team.' },
  { number: '02', title: 'Bring in your people', text: 'Import guest groups, assign access and collect the details you need.' },
  { number: '03', title: 'Coordinate the moving parts', text: 'Plan rooms, travel, tasks, vendors, budgets and communications.' },
  { number: '04', title: 'Run the celebration', text: 'Give every role a current view while GalaGo keeps the shared picture together.' },
];

const Reveal = memo(function Reveal({ children, className = '', delay = 0 }) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial={reduceMotion ? false : { opacity: 0, y: 24 }}
      whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.16 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
});

function SectionHeading({ eyebrow, title, description, align = 'left', inverse = false }) {
  return (
    <div className={`lp-section-heading lp-section-heading--${align} ${inverse ? 'lp-section-heading--inverse' : ''}`}>
      <p className="lp-eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {description && <p className="lp-section-description">{description}</p>}
    </div>
  );
}

function useSectionParallax(distance = 24) {
  const targetRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ['start end', 'end start'],
  });
  const forwardY = useTransform(scrollYProgress, [0, 1], [distance, -distance]);
  const reverseY = useTransform(scrollYProgress, [0, 1], [-distance * 0.55, distance * 0.55]);
  const focalScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.965, 1, 0.982]);

  return {
    targetRef,
    forwardStyle: reduceMotion ? undefined : { y: forwardY },
    focalStyle: reduceMotion ? undefined : { y: forwardY, scale: focalScale },
    reverseStyle: reduceMotion ? undefined : { y: reverseY },
  };
}

function MascotSplash({ onComplete }) {
  return (
    <motion.div
      className="lp-splash"
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22 }}
      role="dialog"
      aria-modal="true"
      aria-label="GalaGo is ready"
    >
      <button type="button" className="lp-splash-skip" onClick={onComplete}>Skip intro</button>
      <motion.div
        className="lp-splash-content"
        initial={{ opacity: 0, y: 18, scale: 0.94 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.img
          src="/assets/landing/galago-mascot-hello.png"
          alt=""
          animate={{ y: [3, 0, 3], rotate: [-1.5, 1.5, -1.5] }}
          transition={{ duration: 1.35, ease: 'easeInOut' }}
        />
        <div className="lp-splash-copy">
          <span>Hello from GalaGo</span>
          <strong>Ready when you are.</strong>
        </div>
      </motion.div>
    </motion.div>
  );
}

function HeroDashboard() {
  const reduceMotion = useReducedMotion();
  const { targetRef, focalStyle, reverseStyle } = useSectionParallax(20);

  return (
    <motion.div
      ref={targetRef}
      className="lp-hero-visual"
      initial={reduceMotion ? false : { opacity: 0, y: 22, rotate: 1.5 }}
      animate={reduceMotion ? undefined : { opacity: 1, y: 0, rotate: 0 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      aria-hidden="true"
    >
      <motion.div className="lp-hero-dashboard-stack" style={focalStyle}>
        <img
          className="lp-hero-mascot-peek"
          src="/assets/landing/galago-mascot-peek-v2.png"
          alt=""
        />
        <div className="lp-dashboard-window">
        <div className="lp-window-bar">
          <span /><span /><span />
          <div className="lp-window-address">galago / event overview</div>
        </div>

        <div className="lp-dashboard-body">
          <aside className="lp-dashboard-sidebar">
            <div className="lp-mini-logo"><GalagoLogo size={26} /></div>
            {[Users, CalendarBlank, Bed, ListChecks, Camera].map((Icon, index) => (
              <div className={`lp-sidebar-icon ${index === 0 ? 'is-active' : ''}`} key={index}>
                <Icon size={16} weight={index === 0 ? 'fill' : 'regular'} />
              </div>
            ))}
          </aside>

          <div className="lp-dashboard-content">
            <div className="lp-dashboard-heading">
              <div>
                <span className="lp-dashboard-kicker">SAMPLE EVENT · EVENT OVERVIEW</span>
                <h3>Anaya & Rohan</h3>
                <p><MapPin size={12} weight="fill" /> Udaipur · 14–16 February</p>
              </div>
              <div className="lp-live-pill"><span /> Live workspace</div>
            </div>

            <div className="lp-stat-grid">
              {[
                ['286', 'Guests', Users],
                ['214', 'Confirmed', CheckCircle],
                ['32', 'Rooms', Bed],
                ['6', 'Functions', Sparkle],
              ].map(([value, label, Icon]) => (
                <div className="lp-stat-card" key={label}>
                  <Icon size={15} />
                  <strong>{value}</strong>
                  <span>{label}</span>
                </div>
              ))}
            </div>

            <div className="lp-dashboard-grid">
              <div className="lp-schedule-card">
                <div className="lp-card-heading">
                  <strong>Today’s run of show</strong>
                  <span>View all</span>
                </div>
                {[
                  ['09:30', 'Guest arrivals', 'Hotel lobby', 'sage'],
                  ['12:00', 'Welcome lunch', 'Lake courtyard', 'gold'],
                  ['18:30', 'Sangeet', 'Zenana Mahal', 'plum'],
                ].map(([time, title, place, tone]) => (
                  <div className="lp-schedule-row" key={title}>
                    <time>{time}</time>
                    <span className={`lp-schedule-dot lp-schedule-dot--${tone}`} />
                    <div><strong>{title}</strong><span>{place}</span></div>
                    <Check size={13} />
                  </div>
                ))}
              </div>

              <div className="lp-action-card">
                <div className="lp-action-icon"><Bell size={16} weight="fill" /></div>
                <span className="lp-card-label">NEXT ACTION</span>
                <strong>12 travel details pending</strong>
                <p>Send a WhatsApp reminder to selected guests.</p>
                <div className="lp-action-button">Review guests <ArrowRight size={12} /></div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </motion.div>

      <motion.div
        className="lp-floating-message"
        style={reverseStyle}
      >
        <div className="lp-floating-message-icon"><WhatsappLogo size={21} weight="fill" /></div>
        <div><strong>Invite delivered</strong><span>to Meera’s family · now</span></div>
        <CheckCircle size={17} weight="fill" />
      </motion.div>

      <div className="lp-visual-note lp-visual-note--bottom"><ChartLineUp size={14} weight="bold" /> Shared event data</div>
    </motion.div>
  );
}

function WebInvitePreview() {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedTemplate = FEATURED_WEB_INVITES[selectedIndex];
  const { targetRef, focalStyle, reverseStyle } = useSectionParallax(14);

  if (!selectedTemplate) return null;

  const selectPrevious = () => {
    setSelectedIndex((current) => (current - 1 + FEATURED_WEB_INVITES.length) % FEATURED_WEB_INVITES.length);
  };

  const selectNext = () => {
    setSelectedIndex((current) => (current + 1) % FEATURED_WEB_INVITES.length);
  };

  return (
    <div ref={targetRef} className="lp-template-browser" aria-label="Browse featured GalaGo web invite templates">
      <div className="lp-template-browser-head">
        <div>
          <span>Browse templates</span>
          <strong>{FEATURED_WEB_INVITES.length} featured designs</strong>
        </div>
        <div className="lp-template-browser-controls">
          <button type="button" onClick={selectPrevious} aria-label="Previous web invite template"><ArrowLeft size={15} /></button>
          <button type="button" onClick={selectNext} aria-label="Next web invite template"><ArrowRight size={15} /></button>
        </div>
      </div>

      <div className="lp-template-browser-stage">
        <motion.div className="lp-webinvite-phone-parallax" style={focalStyle}>
          <div className="lp-webinvite-phone">
            <div className="lp-webinvite-notch" />
            <div className="lp-webinvite-screen">
              <img
                key={selectedTemplate.id}
                src={selectedTemplate.config.layout.background_image_url}
                alt=""
                loading="lazy"
              />
              <div className="lp-webinvite-overlay" />
              <div className="lp-webinvite-copy">
                <p>{selectedTemplate.sample.eyebrow}</p>
                <span>✦</span>
                <strong>{selectedTemplate.sample.headline}</strong>
                <small>{selectedTemplate.sample.date}</small>
                <div className="lp-webinvite-enter">Open the invitation <ArrowRight size={12} /></div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div className="lp-template-active-card" key={selectedTemplate.id} style={reverseStyle}>
          <span>EXISTING TEMPLATE · SAMPLE DATA</span>
          <strong>{selectedTemplate.name}</strong>
          <p>{selectedTemplate.description}</p>
          <div>{selectedTemplate.swatches.map((swatch) => <i key={swatch} style={{ backgroundColor: swatch }} />)}</div>
        </motion.div>
      </div>

      <div className="lp-template-strip">
        {FEATURED_WEB_INVITES.map((template, index) => (
          <button
            type="button"
            className={index === selectedIndex ? 'is-active' : ''}
            key={template.id}
            onClick={() => setSelectedIndex(index)}
            aria-label={`Preview ${template.name}`}
            aria-pressed={index === selectedIndex}
          >
            <img src={template.config.layout.background_image_url} alt="" loading="lazy" />
            <span>{template.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function InviteStudioPreview() {
  const { targetRef, focalStyle, reverseStyle } = useSectionParallax(16);

  return (
    <figure ref={targetRef} className="lp-invite-preview">
      <div className="lp-studio-toolbar">
        <div><MagicWand size={17} weight="fill" /><span>Generated looks</span></div>
        <span>SAMPLE OUTPUT · 2 OF 4</span>
      </div>
      <div className="lp-invite-canvas-wrap">
        <motion.div className="lp-invite-canvas" style={focalStyle}>
          <img src="/assets/landing/invite-studio-baby-shower-v2.jpg" alt="" loading="lazy" />
          <div className="lp-invite-canvas-shade" />
          <div className="lp-invite-canvas-copy">
            <span>A LITTLE ONE IS ON THE WAY · SAMPLE</span>
            <strong>Baby <br />Shower</strong>
            <b>honouring Rhea Mehta</b>
            <small>Sunday, 23 August 2026 · 4 PM<br />Olive House · Mumbai</small>
            <em>Hosted by Naina · RSVP by 10 August<br />Registry details on the event page</em>
          </div>
        </motion.div>
        <motion.div className="lp-invite-direction" style={reverseStyle}>
          <span>Selected direction</span>
          <strong>Moonlit Meadow</strong>
          <p>A complete baby-shower invitation generated from sample event details and creative direction.</p>
          <div className="lp-invite-position">Generated design 2 of 4</div>
        </motion.div>
      </div>
      <figcaption className="lp-studio-footer"><ImageSquare size={16} /><span>Sample generated artwork</span><i /><PaperPlaneTilt size={16} weight="fill" /><span>WhatsApp ready</span></figcaption>
    </figure>
  );
}

function SocialStudioPreview() {
  const { targetRef, focalStyle, reverseStyle } = useSectionParallax(20);

  return (
    <figure ref={targetRef} className="lp-social-preview">
      <motion.div className="lp-social-editor-parallax" style={focalStyle}>
        <div className="lp-social-editor">
          <div className="lp-social-editor-head"><span><ImageSquare size={16} /> Social Studio</span><small>Instagram Story</small></div>
          <div className="lp-social-canvas">
            <img src="/assets/landing/social-studio-save-date-v3.png" alt="" loading="lazy" />
            <div className="lp-social-canvas-shade" />
            <div className="lp-social-headline-frame" aria-hidden="true"><i /></div>
            <div className="lp-social-canvas-copy">
              <span>MILESTONE BIRTHDAY · SAMPLE EVENT</span>
              <strong><em>SAVE THE</em><b>DATE</b></strong>
              <small><i>MAYA'S 60TH BIRTHDAY</i><span>18 OCTOBER 2026 · MUMBAI</span><b>7 DAYS TO GO</b></small>
            </div>
          </div>
          <figcaption className="lp-social-actions"><span>Sample generated design</span><span>Download PNG</span></figcaption>
        </div>
      </motion.div>
      <motion.div className="lp-social-prompt" style={reverseStyle}><Sparkle size={15} weight="fill" /><span>Refine: warmer light and quieter type</span><ArrowRight size={14} /></motion.div>
    </figure>
  );
}

function WhatsAppPreview() {
  const previewRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const isInView = useInView(previewRef, { amount: 0.2 });
  const [chatPhase, setChatPhase] = useState(reduceMotion ? 6 : 0);

  useEffect(() => {
    if (reduceMotion) {
      setChatPhase(6);
      return undefined;
    }

    if (!isInView) {
      setChatPhase(0);
      return undefined;
    }

    const timers = [];
    let cancelled = false;

    const playConversation = () => {
      if (cancelled) return;
      setChatPhase(0);
      timers.push(window.setTimeout(() => setChatPhase(1), 600));
      timers.push(window.setTimeout(() => setChatPhase(2), 1600));
      timers.push(window.setTimeout(() => setChatPhase(3), 2600));
      timers.push(window.setTimeout(() => setChatPhase(4), 3800));
      timers.push(window.setTimeout(() => setChatPhase(5), 4800));
      timers.push(window.setTimeout(() => setChatPhase(6), 5800));
      timers.push(window.setTimeout(playConversation, 7800));
    };

    playConversation();

    return () => {
      cancelled = true;
      timers.forEach(timer => window.clearTimeout(timer));
    };
  }, [isInView, reduceMotion]);

  return (
    <div ref={previewRef} className="lp-whatsapp-preview" aria-hidden="true">
      <div className="lp-whatsapp-orbit lp-whatsapp-orbit--one" />
      <div className="lp-whatsapp-orbit lp-whatsapp-orbit--two" />
      <div className="lp-phone">
        <div className="lp-phone-speaker" />
        <div className="lp-phone-screen">
          <div className="lp-chat-header">
            <ArrowLeft size={15} weight="bold" />
            <div className="lp-chat-avatar">G</div>
            <div><strong>GalaGo Updates</strong><span>{chatPhase === 2 || chatPhase === 5 ? 'typing…' : 'online'}</span></div>
            <WhatsappLogo size={19} weight="fill" />
          </div>
          <div className="lp-chat-day">TODAY</div>
          <div className="lp-chat-thread">
            {chatPhase >= 1 && (
              <motion.div
                className="lp-chat-bubble lp-chat-bubble--outgoing"
                initial={reduceMotion ? false : { opacity: 0, y: 9, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <p>What’s the next event?</p>
                <time>3:42 PM <Check size={11} /><Check size={11} /></time>
              </motion.div>
            )}

            {chatPhase === 2 && (
              <motion.div
                className="lp-chat-bubble lp-chat-bubble--incoming lp-chat-typing"
                initial={reduceMotion ? false : { opacity: 0, y: 7, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.35 }}
              >
                <i /><i /><i />
              </motion.div>
            )}

            {chatPhase >= 3 && (
              <motion.div
                className="lp-chat-bubble lp-chat-bubble--incoming lp-chat-reply"
                initial={reduceMotion ? false : { opacity: 0, y: 10, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <strong>Up next: Sangeet ✨</strong>
                <p>Today at 6:30 PM<br />Zenana Mahal, Udaipur</p>
                <div className="lp-chat-link"><MapPin size={13} weight="fill" /> View itinerary</div>
                <time>3:42 PM</time>
              </motion.div>
            )}

            {chatPhase >= 4 && (
              <motion.div
                className="lp-chat-bubble lp-chat-bubble--outgoing"
                initial={reduceMotion ? false : { opacity: 0, y: 9, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <p>When does my shuttle leave?</p>
                <time>3:43 PM <Check size={11} /><Check size={11} /></time>
              </motion.div>
            )}

            {chatPhase === 5 && (
              <motion.div
                className="lp-chat-bubble lp-chat-bubble--incoming lp-chat-typing"
                initial={reduceMotion ? false : { opacity: 0, y: 7, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.35 }}
              >
                <i /><i /><i />
              </motion.div>
            )}

            {chatPhase >= 6 && (
              <motion.div
                className="lp-chat-bubble lp-chat-bubble--incoming lp-chat-reply lp-chat-reply--compact"
                initial={reduceMotion ? false : { opacity: 0, y: 10, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <strong>5:45 PM from the hotel lobby.</strong>
                <p>We’ll remind you 15 minutes before departure.</p>
                <time>3:43 PM</time>
              </motion.div>
            )}
          </div>
          <div className="lp-chat-compose">
            <span>Message</span>
            <div><PaperPlaneTilt size={15} weight="fill" /></div>
          </div>
        </div>
      </div>

      <div className="lp-delivery-card" data-phase={chatPhase}>
        <span>MESSAGE STATUS · SAMPLE</span>
        <strong>Recipient summary</strong>
        <div className="lp-delivery-progress"><i /></div>
        <p>
          <span>{chatPhase >= 6 ? '4 delivered' : 'Sending…'}</span>
          <span>{chatPhase >= 6 ? '0 pending' : '4 recipients'}</span>
        </p>
      </div>
    </div>
  );
}

function useAiCardScrollStyle(scrollProgress, from, to, reduceMotion) {
  const inputRange = [0.08, 0.58];
  const x = useTransform(scrollProgress, inputRange, [from.x, to.x]);
  const y = useTransform(scrollProgress, inputRange, [from.y, to.y]);
  const scale = useTransform(scrollProgress, inputRange, [from.scale, to.scale]);
  const rotate = useTransform(scrollProgress, inputRange, [from.rotate, to.rotate]);
  const opacity = useTransform(scrollProgress, [0.08, 0.42], [0.72, 1]);

  return reduceMotion ? undefined : { x, y, scale, rotate, opacity };
}

function AiPreview() {
  const previewRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: previewRef,
    offset: ['start end', 'end start'],
  });
  const ticketStyle = useAiCardScrollStyle(
    scrollYProgress,
    { x: 72, y: 88, scale: 0.68, rotate: 9 },
    { x: 16, y: 0, scale: 1, rotate: -5 },
    reduceMotion
  );
  const paletteStyle = useAiCardScrollStyle(
    scrollYProgress,
    { x: 82, y: -34, scale: 0.66, rotate: -10 },
    { x: -18, y: 4, scale: 1, rotate: 6 },
    reduceMotion
  );
  const invoiceStyle = useAiCardScrollStyle(
    scrollYProgress,
    { x: -76, y: -30, scale: 0.68, rotate: -9 },
    { x: 14, y: 0, scale: 1, rotate: 4 },
    reduceMotion
  );

  return (
    <div ref={previewRef} className="lp-ai-preview" aria-hidden="true">
      <div className="lp-ai-grid" />
      <motion.div
        className="lp-ai-card lp-ai-card--ticket"
        style={ticketStyle}
      >
        <div className="lp-ai-card-top"><AirplaneTilt size={17} weight="fill" /><span>SAMPLE TRAVEL READ</span><CheckCircle size={15} weight="fill" /></div>
        <div className="lp-ticket-route"><strong>DEL</strong><i><AirplaneTilt size={15} /></i><strong>UDR</strong></div>
        <div className="lp-ticket-details"><span>14 FEB</span><span>18:35</span><span>AI 482</span></div>
      </motion.div>
      <motion.div
        className="lp-ai-card lp-ai-card--palette"
        style={paletteStyle}
      >
        <div className="lp-ai-card-top"><Palette size={17} weight="fill" /><span>SAMPLE THEME DIRECTION</span></div>
        <strong>Desert dusk</strong>
        <p>Warm, modern, celebratory</p>
        <div className="lp-swatches"><span /><span /><span /><span /></div>
      </motion.div>
      <motion.div
        className="lp-ai-card lp-ai-card--invoice"
        style={invoiceStyle}
      >
        <div className="lp-ai-card-top"><Receipt size={17} weight="fill" /><span>SAMPLE INVOICE READ</span></div>
        <div className="lp-invoice-row"><span>Category</span><strong>Décor</strong></div>
        <div className="lp-invoice-row"><span>Amount</span><strong>₹ 1,24,000</strong></div>
        <div className="lp-ai-review"><Check size={13} /> Ready for review</div>
      </motion.div>
      <div className="lp-ai-spark lp-ai-spark--one"><Sparkle size={18} weight="fill" /></div>
      <div className="lp-ai-spark lp-ai-spark--two"><Sparkle size={13} weight="fill" /></div>
    </div>
  );
}

function WorkflowStep({ step, index, scrollProgress, reduceMotion }) {
  const start = 0.28 + (index * 0.16);
  const nodePeak = start + 0.055;
  const nodeEnd = start + 0.12;
  const lineStart = start + 0.07;
  const lineEnd = start + 0.19;
  const nodeScale = useTransform(scrollProgress, [start, nodePeak, nodeEnd], [0.74, 1.42, 1]);
  const pulseScale = useTransform(scrollProgress, [start, nodePeak, nodeEnd], [0.7, 1.9, 1.15]);
  const pulseOpacity = useTransform(scrollProgress, [start, nodePeak, nodeEnd], [0, 0.48, 0]);
  const nodeColor = useTransform(scrollProgress, [start, nodeEnd], ['#cbbfb1', '#b64e39']);
  const numberColor = useTransform(scrollProgress, [start, nodeEnd], ['#a99d90', '#b8614d']);
  const contentOpacity = useTransform(scrollProgress, [start, nodeEnd], [0.38, 1]);
  const contentY = useTransform(scrollProgress, [start, nodeEnd], [10, 0]);
  const lineScale = useTransform(scrollProgress, [lineStart, lineEnd], [0, 1]);
  const signalPosition = useTransform(scrollProgress, [lineStart, lineEnd], ['0%', '94%']);
  const signalOpacity = useTransform(
    scrollProgress,
    [lineStart, lineStart + 0.018, lineEnd - 0.018, lineEnd],
    [0, 1, 1, 0]
  );

  return (
    <motion.div
      className="lp-workflow-step"
      style={reduceMotion ? undefined : { opacity: contentOpacity, y: contentY }}
    >
      <motion.span style={reduceMotion ? undefined : { color: numberColor }}>{step.number}</motion.span>
      <div className="lp-workflow-line">
        <motion.i style={reduceMotion ? undefined : { backgroundColor: nodeColor, scale: nodeScale }} />
        <motion.span
          className="lp-workflow-node-pulse"
          aria-hidden="true"
          style={reduceMotion ? undefined : { opacity: pulseOpacity, scale: pulseScale }}
        />
        <motion.em
          className="lp-workflow-progress lp-workflow-progress--horizontal"
          aria-hidden="true"
          style={reduceMotion ? undefined : { scaleX: lineScale }}
        />
        <motion.em
          className="lp-workflow-progress lp-workflow-progress--vertical"
          aria-hidden="true"
          style={reduceMotion ? undefined : { scaleY: lineScale }}
        />
        <motion.b
          className="lp-workflow-signal lp-workflow-signal--horizontal"
          aria-hidden="true"
          style={reduceMotion ? undefined : { left: signalPosition, opacity: signalOpacity }}
        />
        <motion.b
          className="lp-workflow-signal lp-workflow-signal--vertical"
          aria-hidden="true"
          style={reduceMotion ? undefined : { top: signalPosition, opacity: signalOpacity }}
        />
      </div>
      <h3>{step.title}</h3>
      <p>{step.text}</p>
    </motion.div>
  );
}

function useFeatureFolioValue(scrollProgress, index, total, upcomingValue, activeValue, completedValue) {
  const lastIndex = Math.max(1, total - 1);
  const step = 1 / lastIndex;
  const focusPoint = index / lastIndex;
  let inputRange;
  let outputRange;

  if (index === 0) {
    inputRange = [0, Math.min(1, step * 0.78), 1];
    outputRange = [activeValue, completedValue, completedValue];
  } else if (index === total - 1) {
    inputRange = [0, Math.max(0, focusPoint - (step * 0.58)), focusPoint];
    outputRange = [upcomingValue, upcomingValue, activeValue];
  } else {
    inputRange = [0, focusPoint - (step * 0.58), focusPoint, focusPoint + (step * 0.78), 1];
    outputRange = [upcomingValue, upcomingValue, activeValue, completedValue, completedValue];
  }

  return useTransform(scrollProgress, inputRange, outputRange);
}

function FeatureFolioCard({ feature, index, total, scrollProgress, reduceMotion }) {
  const completedY = -145 + (index * 15);
  const upcomingY = 205 + (Math.min(index, 3) * 8);
  const upcomingRotate = index % 2 === 0 ? -2.8 : 2.8;
  const y = useFeatureFolioValue(scrollProgress, index, total, upcomingY, 0, completedY);
  const rotate = useFeatureFolioValue(scrollProgress, index, total, upcomingRotate, 0, 0);
  const scale = useFeatureFolioValue(scrollProgress, index, total, 0.94, 1, 0.98);
  const opacity = useFeatureFolioValue(scrollProgress, index, total, 0.72, 1, 1);
  const clipPath = useFeatureFolioValue(
    scrollProgress,
    index,
    total,
    'inset(0 0 0% 0 round 20px)',
    'inset(0 0 0% 0 round 20px)',
    'inset(0 0 242px 0 round 20px)',
  );
  const bodyOpacity = useFeatureFolioValue(scrollProgress, index, total, 0.26, 1, 0);
  const tabOpacity = useFeatureFolioValue(scrollProgress, index, total, 0, 0, 1);

  return (
    <motion.article
      className={`lp-feature-folio-card lp-feature-card--${feature.tone}`}
      style={reduceMotion ? undefined : {
        y,
        rotate,
        scale,
        opacity,
        clipPath,
        zIndex: index + 1,
      }}
    >
      <motion.div className="lp-feature-folio-tab" style={reduceMotion ? undefined : { opacity: tabOpacity }}>
        <span>{String(index + 1).padStart(2, '0')}</span>
        <strong>{feature.title}</strong>
        <Check size={13} weight="bold" />
      </motion.div>
      <motion.div className="lp-feature-folio-body" style={reduceMotion ? undefined : { opacity: bodyOpacity }}>
        <div className="lp-feature-folio-topline">
          <div className="lp-feature-icon"><feature.icon size={24} weight="duotone" /></div>
          <span>{String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
        </div>
        <div>
          <h3>{feature.title}</h3>
          <p>{feature.description}</p>
        </div>
        <span className="lp-feature-detail">{feature.detail}</span>
      </motion.div>
    </motion.article>
  );
}

function MobileFeatureFolio({ items }) {
  const storyRef = useRef(null);
  const rangeRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const scrollDistance = Math.max(1080, (items.length - 1) * 180);
  const { scrollYProgress } = useScroll({
    target: rangeRef,
    offset: ['start center', 'end center'],
  });

  return (
    <div
      ref={storyRef}
      className="lp-feature-folio"
      style={{ '--lp-feature-folio-distance': `${scrollDistance}px` }}
    >
      <div ref={rangeRef} className="lp-feature-folio-range" aria-hidden="true" />
      <div className="lp-feature-folio-sticky">
        <div className="lp-feature-folio-meta">
          <span>Event folio · scroll to open</span>
          <div aria-hidden="true"><motion.i style={reduceMotion ? undefined : { scaleX: scrollYProgress }} /></div>
          <small>01—{String(items.length).padStart(2, '0')}</small>
        </div>
        <div className="lp-feature-folio-stack">
          <div className="lp-feature-folio-spine" aria-hidden="true" />
          {items.map((feature, index) => (
            <FeatureFolioCard
              key={feature.title}
              feature={feature}
              index={index}
              total={items.length}
              scrollProgress={scrollYProgress}
              reduceMotion={reduceMotion}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileScrollRail({ children, trackClassName, hint, inverse = false, itemCount, ariaLabel }) {
  const storyRef = useRef(null);
  const rangeRef = useRef(null);
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const [travel, setTravel] = useState(0);
  const { scrollYProgress } = useScroll({
    target: rangeRef,
    offset: ['start center', 'end center'],
  });
  const x = useTransform(scrollYProgress, [0, 1], [0, -travel]);

  useEffect(() => {
    const measure = () => {
      if (!viewportRef.current || !trackRef.current) return;
      setTravel(Math.max(0, trackRef.current.scrollWidth - viewportRef.current.clientWidth));
    };

    measure();
    window.addEventListener('resize', measure);

    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (observer) {
      observer.observe(viewportRef.current);
      observer.observe(trackRef.current);
    }

    return () => {
      window.removeEventListener('resize', measure);
      if (observer) observer.disconnect();
    };
  }, [itemCount]);

  const scrollDistance = Math.max(480, Math.round(travel * 0.42));
  const artOpacity = useTransform(scrollYProgress, [0, 0.08, 0.9, 1], [0, 1, 1, 0]);
  const magicOpacity = useTransform(scrollYProgress, [0, 0.015, 0.94, 1], [0.72, 1, 1, 0]);
  const journeyY = useTransform(scrollYProgress, [0, 1], [70, -70]);
  const wandDistance = useTransform(scrollYProgress, [0, 0.04, 0.16, 0.28, 0.48, 0.68, 0.88, 1], ['0%', '5%', '12%', '9%', '42%', '70%', '91%', '100%']);
  const wandWristRotate = useTransform(scrollYProgress, [0, 0.16, 0.28, 0.48, 0.68, 0.88, 1], [-3, 0, -8, -1, 4, -2, 1]);
  const wandScale = useTransform(scrollYProgress, [0, 0.1, 0.28, 0.58, 0.76, 0.92, 1], [0.88, 1, 0.96, 1.08, 1.1, 1, 0.9]);
  const wandBlur = useTransform(scrollYProgress, [0, 0.28, 0.42, 0.58, 0.76, 0.9, 1], ['blur(0px)', 'blur(0px)', 'blur(1.8px)', 'blur(0.4px)', 'blur(1.2px)', 'blur(0px)', 'blur(0px)']);
  const spellTrailLength = useTransform(scrollYProgress, [0, 0.05, 0.16, 0.28, 0.48, 0.68, 0.88, 1], [0, 0.01, 0.08, 0.08, 0.3, 0.56, 0.8, 1]);
  const spellTrailOpacity = useTransform(scrollYProgress, [0, 0.04, 0.16, 0.9, 1], [0, 0.25, 0.82, 0.78, 0]);
  const magicScale = useTransform(scrollYProgress, [0, 0.08, 0.3, 0.58, 0.78, 1], [0.45, 0.6, 0.82, 1, 1.1, 0.78]);
  const impactScale = useTransform(scrollYProgress, [0, 0.5, 0.62, 0.78, 1], [0, 0, 1, 1.45, 1.7]);
  const impactOpacity = useTransform(scrollYProgress, [0, 0.5, 0.62, 0.82, 1], [0, 0, 0.9, 0.28, 0]);

  return (
    <div
      ref={storyRef}
      className={`lp-mobile-scroll-story ${inverse ? 'lp-mobile-scroll-story--inverse' : ''}`}
      style={{ '--lp-mobile-scroll-distance': `${scrollDistance}px` }}
    >
      <div ref={rangeRef} className="lp-mobile-scroll-range" aria-hidden="true" />
      <div className="lp-mobile-story-art" aria-hidden="true">
        {inverse ? (
          <motion.div className="lp-mobile-magic-scene" style={reduceMotion ? undefined : { opacity: magicOpacity }}>
            <svg viewBox="0 0 360 680" role="presentation">
              <motion.path
                className="lp-mobile-spell-trail lp-mobile-spell-trail--glow"
                d="M-70 620 C-8 604 52 568 74 512 C104 440 45 382 70 315 C101 236 210 210 281 266 C350 320 331 420 260 458 C193 493 111 452 108 386 C105 323 176 288 228 327 C276 362 265 432 214 451 C171 467 147 434 162 392 C181 338 253 311 297 248 C332 198 319 121 272 70 C244 39 218 4 190-82"
                style={reduceMotion ? undefined : { pathLength: spellTrailLength, opacity: spellTrailOpacity }}
              />
              <motion.path
                className="lp-mobile-spell-trail lp-mobile-spell-trail--core"
                d="M-70 620 C-8 604 52 568 74 512 C104 440 45 382 70 315 C101 236 210 210 281 266 C350 320 331 420 260 458 C193 493 111 452 108 386 C105 323 176 288 228 327 C276 362 265 432 214 451 C171 467 147 434 162 392 C181 338 253 311 297 248 C332 198 319 121 272 70 C244 39 218 4 190-82"
                style={reduceMotion ? undefined : { pathLength: spellTrailLength, opacity: spellTrailOpacity }}
              />
            </svg>
            <motion.div
              className="lp-mobile-magic-wand"
              style={reduceMotion ? undefined : { offsetDistance: wandDistance, rotate: wandWristRotate, scale: wandScale }}
            >
              <motion.img
                src="/assets/landing/ai-wand-realistic.png"
                alt=""
                draggable="false"
                style={reduceMotion ? undefined : { filter: wandBlur }}
              />
            </motion.div>
            <motion.div className="lp-mobile-magic-particles" style={reduceMotion ? undefined : { scale: magicScale, opacity: spellTrailOpacity }}>
              <span /><span /><span /><span /><span /><span /><span /><span />
            </motion.div>
            <motion.div className="lp-mobile-magic-impact" style={reduceMotion ? undefined : { scale: impactScale, opacity: impactOpacity }}>
              <i />
              <Sparkle size={34} weight="fill" />
            </motion.div>
          </motion.div>
        ) : (
          <motion.div className="lp-mobile-journey-scene" style={reduceMotion ? undefined : { y: journeyY, opacity: artOpacity }}>
            <svg viewBox="0 0 360 620" role="presentation">
              <motion.path
                d="M180 8 C72 78 74 168 184 218 C304 273 292 371 178 410 C66 448 66 544 180 612"
                style={reduceMotion ? undefined : { pathLength: scrollYProgress }}
              />
            </svg>
            <span className="lp-mobile-journey-stop lp-mobile-journey-stop--guests"><Users size={21} weight="fill" /></span>
            <span className="lp-mobile-journey-stop lp-mobile-journey-stop--date"><CalendarBlank size={20} weight="fill" /></span>
            <span className="lp-mobile-journey-stop lp-mobile-journey-stop--stay"><Bed size={21} weight="fill" /></span>
            <span className="lp-mobile-journey-stop lp-mobile-journey-stop--tasks"><ListChecks size={20} weight="fill" /></span>
            <span className="lp-mobile-journey-stop lp-mobile-journey-stop--budget"><Wallet size={20} weight="fill" /></span>
          </motion.div>
        )}
      </div>
      <div className="lp-mobile-scroll-sticky">
        <p className={`lp-mobile-swipe-hint ${inverse ? 'lp-mobile-swipe-hint--inverse' : ''}`}>
          {hint} <ArrowRight size={14} />
        </p>
        <div className="lp-mobile-rail-progress" aria-hidden="true">
          <motion.i style={reduceMotion ? undefined : { scaleX: scrollYProgress }} />
        </div>
        <div ref={viewportRef} className="lp-mobile-scroll-viewport" tabIndex="0" aria-label={ariaLabel}>
          <motion.div
            ref={trackRef}
            className={trackClassName}
            style={reduceMotion ? undefined : { x }}
          >
            {children}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default function LandingPageV3() {
  const reduceMotion = useReducedMotion();
  const workflowRef = useRef(null);
  const { scrollYProgress: workflowScrollProgress } = useScroll({
    target: workflowRef,
    offset: ['start end', 'end start'],
  });
  const [showSplash, setShowSplash] = useState(shouldShowMascotSplash);

  useEffect(() => {
    if (!showSplash) return undefined;

    const timer = window.setTimeout(() => setShowSplash(false), 1800);
    return () => window.clearTimeout(timer);
  }, [showSplash]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'GalaGo — Event planning, guest management & operations';
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <div className="galago-landing">
      <AnimatePresence>
        {showSplash && <MascotSplash onComplete={() => setShowSplash(false)} />}
      </AnimatePresence>
      <a className="lp-skip-link" href="#main-content">Skip to content</a>

      <header className="lp-header">
        <nav className="lp-nav" aria-label="Main navigation">
          <Link to="/" className="lp-brand" aria-label="GalaGo home">
            <span className="lp-brand-mark"><GalagoLogo size={34} /></span>
            <span className="lp-brand-name">Gala<span>Go</span></span>
          </Link>

          <div className="lp-nav-links">
            <a href="#features">Features</a>
            <a href="#web-invites">Creative tools</a>
            <a href="#whatsapp">WhatsApp</a>
            <a href="#ai">AI tools</a>
            <a href="#for-everyone">Who it’s for</a>
          </div>

          <div className="lp-nav-actions">
            <Link to="/login" className="lp-nav-login">Sign in</Link>
            <Link to="/register" className="lp-button lp-button--small">
              Start planning <ArrowUpRight size={15} weight="bold" />
            </Link>
          </div>
        </nav>
      </header>

      <main id="main-content">
        <section className="lp-hero">
          <div className="lp-hero-pattern" aria-hidden="true" />
          <div className="lp-container lp-hero-grid">
            <motion.div
              className="lp-hero-copy"
              initial={reduceMotion ? false : { opacity: 0, y: 18 }}
              animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="lp-hero-badge"><Sparkle size={14} weight="fill" /> Weddings, birthdays, baby showers & corporate events</div>
              <h1>Event operations,<br /><em>beautifully under control.</em></h1>
              <p className="lp-hero-description">
                GalaGo connects planners, hosts and guests across invitations, RSVPs, rooms, travel, timelines, vendors, budgets and photos—so every moving part stays in step.
              </p>

              <div className="lp-hero-actions">
                <Link to="/register" className="lp-button lp-button--primary">
                  Plan your event <ArrowRight size={18} weight="bold" />
                </Link>
                <a href="#features" className="lp-button lp-button--secondary">
                  Explore the platform
                </a>
              </div>

              <div className="lp-hero-checks" aria-label="Platform highlights">
                {['Built for multi-day events', 'Planner, host & guest views', 'WhatsApp connected'].map(item => (
                  <span key={item}><CheckCircle size={16} weight="fill" /> {item}</span>
                ))}
              </div>
            </motion.div>

            <HeroDashboard />
          </div>
        </section>

        <section className="lp-proof" aria-label="GalaGo platform summary">
          <div className="lp-container lp-proof-grid">
            <div><strong>One</strong><span>shared source of truth</span></div>
            <div><strong>Role-based</strong><span>planner, host & guest views</span></div>
            <div><strong>Four</strong><span>celebration types supported</span></div>
            <div className="lp-proof-quote"><Sparkle size={18} weight="fill" /><span>Made for the details behind unforgettable events.</span></div>
          </div>
        </section>

        <section className="lp-section lp-features" id="features">
          <div className="lp-container">
            <Reveal>
              <SectionHeading
                eyebrow="The connected event workspace"
                title={<>From the first guest list<br />to the final photo.</>}
                description="Plan the celebration and run the operation without losing context between spreadsheets, chats and separate tools."
              />
            </Reveal>

            <div className="lp-feature-grid lp-feature-grid--desktop">
              {features.map((feature, index) => (
                  <Reveal
                    key={feature.title}
                    className={`lp-feature-card lp-feature-card--${feature.tone} ${feature.size ? `lp-feature-card--${feature.size}` : ''}`}
                    delay={(index % 4) * 0.06}
                  >
                    <div className="lp-feature-icon"><feature.icon size={24} weight="duotone" /></div>
                    <div>
                      <h3>{feature.title}</h3>
                      <p>{feature.description}</p>
                    </div>
                    <span className="lp-feature-detail">{feature.detail}</span>
                  </Reveal>
                ))}
            </div>

            <MobileFeatureFolio items={features} />
          </div>
        </section>

        <section className="lp-section lp-web-invites" id="web-invites">
          <div className="lp-container lp-creative-grid">
            <Reveal className="lp-creative-copy">
              <div className="lp-story-icon lp-story-icon--web"><DeviceMobile size={23} weight="duotone" /></div>
              <SectionHeading
                eyebrow="Web invite templates"
                title={<>An invitation that<br />keeps the story together.</>}
                description="For weddings and birthdays, begin with a built-in design and turn live event details into a polished, shareable guest destination."
              />
              <div className="lp-check-list lp-check-list--creative">
                {[
                  'Preview a template before personalizing it',
                  'Arrange sections for itinerary, venues, maps, gallery, RSVP and more',
                  'Customize colors, typography, opening effects, imagery and music',
                  'Save named designs, share the active link and restore earlier revisions',
                ].map(item => (
                  <div key={item}><span><Check size={14} weight="bold" /></span><p>{item}</p></div>
                ))}
              </div>
              <p className="lp-availability-note"><ShieldCheck size={16} /> Currently enabled for wedding and birthday events.</p>
            </Reveal>
            <Reveal className="lp-creative-visual" delay={0.08}>
              <WebInvitePreview />
            </Reveal>
          </div>
        </section>

        <section className="lp-section lp-invite-studio" id="invite-studio">
          <div className="lp-container lp-creative-grid lp-creative-grid--reverse">
            <Reveal className="lp-creative-visual">
              <InviteStudioPreview />
            </Reveal>
            <Reveal className="lp-creative-copy" delay={0.08}>
              <div className="lp-story-icon lp-story-icon--invite"><MagicWand size={23} weight="duotone" /></div>
              <SectionHeading
                eyebrow="Invite Studio"
                title={<>Create the invite.<br />Choose the final look.</>}
                description="Generate image invites from the event or selected functions, then compare the results and decide exactly which design becomes the primary invite."
              />
              <div className="lp-creative-points">
                <div><ImageSquare size={20} weight="duotone" /><p><strong>Guide the art direction</strong><span>Add a people photo, an optional inspiration design and your own notes.</span></p></div>
                <div><Sparkle size={20} weight="duotone" /><p><strong>Review generated choices</strong><span>Compare available looks, revisit generation history and select the primary design.</span></p></div>
                <div><WhatsappLogo size={20} weight="duotone" /><p><strong>Use it in guest outreach</strong><span>The selected invite media is available to the WhatsApp invitation workflow.</span></p></div>
              </div>
              <p className="lp-availability-note"><CheckCircle size={16} /> Available across all four supported event types.</p>
            </Reveal>
          </div>
        </section>

        <section className="lp-section lp-social-studio" id="social-studio">
          <div className="lp-container lp-creative-grid">
            <Reveal className="lp-creative-copy">
              <div className="lp-story-icon lp-story-icon--social"><ImageSquare size={23} weight="duotone" /></div>
              <SectionHeading
                eyebrow="Social Studio"
                title={<>Turn event details into<br />ready-to-share artwork.</>}
                description="Create an Instagram Story design from a people photo, the event context, an optional inspiration image and a written creative direction."
              />
              <div className="lp-check-list lp-check-list--creative">
                {[
                  'Keep the uploaded people and recognizable faces central to the result',
                  'Generate a fresh direction or request a written refinement',
                  'Move between the generated designs before choosing one',
                  'Share from a supported device or download the finished PNG',
                ].map(item => (
                  <div key={item}><span><Check size={14} weight="bold" /></span><p>{item}</p></div>
                ))}
              </div>
              <p className="lp-availability-note"><CheckCircle size={16} /> Available across all four supported event types.</p>
            </Reveal>
            <Reveal className="lp-creative-visual" delay={0.08}>
              <SocialStudioPreview />
            </Reveal>
          </div>
        </section>

        <section className="lp-section lp-whatsapp" id="whatsapp">
          <div className="lp-container lp-story-grid">
            <Reveal className="lp-story-visual-wrap">
              <WhatsAppPreview />
            </Reveal>

            <Reveal className="lp-story-copy" delay={0.08}>
              <div className="lp-story-icon lp-story-icon--whatsapp"><WhatsappLogo size={23} weight="fill" /></div>
              <SectionHeading
                eyebrow="WhatsApp messaging"
                title={<>Meet guests where<br />they already are.</>}
                description="Move essential event communication out of scattered group chats. GalaGo turns current event data into focused, trackable WhatsApp messages."
              />

              <div className="lp-check-list">
                {whatsappBenefits.map(item => (
                  <div key={item}><span><Check size={14} weight="bold" /></span><p>{item}</p></div>
                ))}
              </div>

              <div className="lp-story-note">
                <ShieldCheck size={20} weight="duotone" />
                <p><strong>Intentional by design.</strong> Review the template and recipients before sending; automated reminders run only when configured.</p>
              </div>
            </Reveal>
          </div>

          <div className="lp-container lp-message-flow" aria-label="WhatsApp messaging workflow">
            {[
              ['01', 'Choose the moment', 'Invite, RSVP, travel, itinerary, room or photo update.'],
              ['02', 'Check the audience', 'Use current guest data and review who will receive it.'],
              ['03', 'Send with confidence', 'Dispatch an approved template and keep an attempt log.'],
            ].map(([number, title, text], index) => (
              <React.Fragment key={number}>
                <Reveal className="lp-flow-step" delay={index * 0.08}>
                  <span>{number}</span><div><strong>{title}</strong><p>{text}</p></div>
                </Reveal>
                {index < 2 && <ArrowRight className="lp-flow-arrow" size={20} aria-hidden="true" />}
              </React.Fragment>
            ))}
          </div>
        </section>

        <section className="lp-section lp-ai" id="ai">
          <div className="lp-ai-glow" aria-hidden="true" />
          <div className="lp-container lp-story-grid lp-story-grid--ai">
            <Reveal className="lp-story-copy">
              <div className="lp-story-icon lp-story-icon--ai"><Brain size={23} weight="duotone" /></div>
              <SectionHeading
                eyebrow="GalaGo AI"
                title={<>Less data entry.<br />More human judgment.</>}
                description="Use AI to read travel tickets and invoices, shape themes, assist with budgets, moderate guest photos and draft event stories."
                inverse
              />
              <div className="lp-ai-principle">
                <Sparkle size={19} weight="fill" />
                <div><strong>Assistance with clear boundaries.</strong><p>Creative suggestions and extracted details remain reviewable; photo-wall checks run automatically before gallery publication.</p></div>
              </div>
            </Reveal>

            <Reveal className="lp-story-visual-wrap" delay={0.08}>
              <AiPreview />
            </Reveal>
          </div>

          <div className="lp-container">
            <MobileScrollRail
              trackClassName="lp-ai-capability-grid"
              hint="Scroll through AI tools"
              inverse
              itemCount={aiCapabilities.length}
              ariaLabel="GalaGo AI tools"
            >
              {aiCapabilities.map((capability, index) => (
                <Reveal className="lp-ai-capability" key={capability.title} delay={index * 0.06}>
                  <capability.icon size={23} weight="duotone" />
                  <h3>{capability.title}</h3>
                  <p>{capability.description}</p>
                  <AiCapabilityPreview type={capability.preview} />
                </Reveal>
              ))}
            </MobileScrollRail>
          </div>
        </section>

        <section className="lp-section lp-audiences" id="for-everyone">
          <div className="lp-container">
            <Reveal>
              <SectionHeading
                eyebrow="One event, thoughtfully shared"
                title={<>The right view for<br />every person involved.</>}
                description="Everyone works from the same event, but nobody has to navigate tools or details that do not belong to their role."
                align="center"
              />
            </Reveal>

            <div className="lp-audience-grid">
              {audiences.map((audience, index) => (
                <Reveal className={`lp-audience-card lp-audience-card--${audience.tone}`} key={audience.label} delay={index * 0.08}>
                  <div className="lp-audience-top">
                    <span className="lp-audience-icon"><audience.icon size={24} weight="duotone" /></span>
                    <span className="lp-audience-label">{audience.label}</span>
                  </div>
                  <h3>{audience.title}</h3>
                  <p>{audience.description}</p>
                  <ul>
                    {audience.items.map(item => <li key={item}><Check size={13} weight="bold" /> {item}</li>)}
                  </ul>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section ref={workflowRef} className="lp-section lp-workflow">
          <div className="lp-container">
            <Reveal>
              <SectionHeading
                eyebrow="How GalaGo fits"
                title="A calmer path from planning to showtime."
                description="Start with the event, connect the people, then let every operational detail build on the same foundation."
              />
            </Reveal>

            <div className="lp-workflow-grid">
              {workflow.map((step, index) => (
                <WorkflowStep
                  step={step}
                  index={index}
                  scrollProgress={workflowScrollProgress}
                  reduceMotion={reduceMotion}
                  key={step.number}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="lp-final-cta">
          <div className="lp-cta-orb lp-cta-orb--one" aria-hidden="true" />
          <div className="lp-cta-orb lp-cta-orb--two" aria-hidden="true" />
          <div className="lp-container">
            <Reveal className="lp-cta-content">
              <div className="lp-cta-logo"><GalagoLogo size={48} /></div>
              <p className="lp-eyebrow">Your next celebration</p>
              <h2>Keep the joy.<br /><em>Lose the operational noise.</em></h2>
              <p>Bring your guests, team and event details into one beautifully organized workspace.</p>
              <div className="lp-hero-actions lp-hero-actions--center">
                <Link to="/register" className="lp-button lp-button--primary lp-button--light">
                  Start planning <ArrowRight size={18} weight="bold" />
                </Link>
                <a href="mailto:hello@galago.in" className="lp-button lp-button--ghost">Talk to GalaGo</a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-container lp-footer-main">
          <div>
            <Link to="/" className="lp-brand" aria-label="GalaGo home">
              <span className="lp-brand-mark"><GalagoLogo size={31} /></span>
              <span className="lp-brand-name">Gala<span>Go</span></span>
            </Link>
            <p>Celebration technology for planners, hosts and guests.</p>
          </div>
          <div className="lp-footer-links">
            <div><strong>Explore</strong><a href="#features">Features</a><a href="#whatsapp">WhatsApp</a><a href="#ai">AI tools</a></div>
            <div><strong>Connect</strong><a href="mailto:hello@galago.in">hello@galago.in</a><a href="https://instagram.com/galago.official" target="_blank" rel="noreferrer">Instagram</a></div>
            <div><strong>Account</strong><Link to="/login">Sign in</Link><Link to="/register">Get started</Link><Link to="/privacy-policy">Privacy</Link></div>
          </div>
        </div>
        <div className="lp-container lp-footer-bottom"><span>© 2026 GalaGo</span><span>Plan well. Celebrate fully.</span></div>
      </footer>
    </div>
  );
}
